import Route from 'cio/routes/base';
import config from 'cio/config/environment';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { later, cancel } from '@ember/runloop';
import AuthenticatedController from 'cio/pods/authenticated/controller';
import AccountModel from 'cio/models/account';
import AuthService from 'cio/services/auth';
import AccountService from 'cio/services/account';
import RavenService from 'cio/services/raven';
import FeaturesService from 'ember-feature-flags/services/features';
import AnalyticsService from 'cio/services/analytics';
import SignupService from 'cio/services/signup';
import DS from 'ember-data';
import MasqService from 'cio/services/masq';
import RouterService from '@ember/routing/router-service';
import Transition from '@ember/routing/-private/transition';
import tracking from 'cio/utils/tracking-scripts';
import SessionService from 'cio/services/session';
import { EmberRunTimer } from '@ember/runloop/types';
import { setAccountIdCookie } from 'cio/utils/auth-cookies';
import UserModel from 'cio/models/user';
import HydraService, { HydraWorkspace } from 'cio/services/hydra';
import { typeOf } from '@ember/utils';
import ApiService from 'cio/services/api';
import EnvironmentService from 'cio/services/environment';

const bodyTimeoutClass = 'did-timeout';

export default class AuthenticatedRoute extends Route {
  @service account!: AccountService;
  @service analytics!: AnalyticsService;
  @service api!: ApiService;
  @service auth!: AuthService;
  @service environment!: EnvironmentService;
  @service features!: FeaturesService;
  @service hydra!: HydraService;
  @service masq!: MasqService;
  @service persistentFeatures: any;
  @service raven!: RavenService;
  @service router!: RouterService;
  @service satismeter: any;
  @service session!: SessionService;
  @service settings: any;
  @service signup!: SignupService;
  @service store!: DS.Store;

  accountTimeoutTimer!: EmberRunTimer;

  deactivate(): void {
    super.deactivate();
    this.releaseNotification.fetch.cancelAll();
  }

  beforeModel(transition: Transition) {
    this.session.requireAuthentication(transition, 'login');

    return super.beforeModel(transition);
  }

  async model(params: { masqAccountID: string | null }, transition: Transition) {
    // after signing-up and being redirected to "confirm your account"
    // notification page, account is already in the store - pushed there from
    // sign-up API endpoint response
    //
    // return it instead of executing API request to "/account/current"
    // endpoint - which will result in server error response directly after
    // sign-up, due to database tables not being ready yet
    let existingAccounts = this.store.peekAll('account') as DS.RecordArray<AccountModel>;
    let existingAccount = existingAccounts.get('firstObject');

    if (existingAccount) {
      return existingAccount;
    }

    this.setupTimeout();

    let { masqAccountID } = params;

    let environmentID = '';

    // try to find the environment route and extract the `environment_id` param.
    // we cannot use `paramsFor` since the environments route is not initialised
    // yet, so we filter the `environment` route from transition.routeInfos.
    //
    // @ts-ignore routeInfos is not exported on the transition type
    let route = transition.routeInfos?.find((route) => route.name === 'environment');
    if (route?.params) {
      environmentID = route.params.environment_id;
    }

    // translate 'last' to an `environment_id` if applicable
    if (environmentID === 'last' && transition != null) {
      let account = await this.store.queryRecord('account', {});
      let user = account.belongsTo('currentUser').value() as UserModel | null;
      let lastEnvironmentId = user?.belongsTo('lastEnvironment').id() ?? null;

      if (lastEnvironmentId != null) {
        let paramValues = transition.to?.params ?? {};
        let paramNames = transition.to?.paramNames ?? [];
        let route = transition.to.parent;

        while (route != null) {
          paramValues = { ...paramValues, ...route.params };
          paramNames = [...route.paramNames.reverse(), ...paramNames];
          route = route.parent;
        }

        let mappedParamNames = paramNames.filter((p) => p !== 'environment_id').map((p) => paramValues[p]);

        let targetParamValues = [lastEnvironmentId, ...mappedParamNames].filter((item) => item != null);

        // @ts-ignore urlFor typing doesn't support destructured argument
        let targetUrl = this.router.urlFor(transition.to.name, ...targetParamValues, {
          queryParams: { ...transition.to.queryParams },
        });

        // https://github.com/emberjs/ember.js/issues/18422
        // transitionTo doesn't work correctly if supplied with a URL that
        // includes the rootURL, so we need to remove the rootURL if present.
        let { rootURL } = this.router;
        if (rootURL != null && rootURL !== '/' && targetUrl.startsWith(rootURL)) {
          targetUrl = targetUrl.slice(rootURL.length);
          if (!targetUrl.startsWith('/')) {
            targetUrl = `/${targetUrl}`;
          }
        }

        return this.router.transitionTo(targetUrl);
      }
    }

    if (masqAccountID) {
      // ?masq_account_id=... query param present: load account by its ID
      return this.store.queryRecord('account', { accountID: masqAccountID });
    } else if (environmentID) {
      // in environment route: load account by env. ID
      // (will also work properly when masqing by env.)
      try {
        return await this.store.queryRecord('account', { environmentID });
      } catch (err) {
        // when we get a 403 response: the environment is likely not
        // accessible, but there may be other environments, and the account may
        // still be accessible, so just fall through to load the current user's
        // account. Rethrow any other errors.
        let hasErrors = err?.errors != null && typeOf(err.errors) === 'array' && err.errors.length > 0;
        let has403Error = hasErrors && err.errors[0].status === '403';
        if (!has403Error) {
          throw err;
        }
      }
    }

    // fallback: load current user's account
    return this.store.queryRecord('account', {});
  }

  async afterModel(account: AccountModel) {
    if (config.isProdStaging && account.isDataCenterEU) {
      // prevent dirty failure because prod-staging doesn't have an UI API EU instance:
      alert("Production staging currently doesn't support EU datacenter accounts.");
      window.location.href = window.location.origin;
      return;
    }

    // In the scenario that the account took longer than
    // the timeout but did eventually resolve, we have to
    // undo what we did in the timeout handler.
    this.clearTimeout();

    this.account.set('current', account);
    this.auth.set('user', account.currentUser);
    setAccountIdCookie(account.currentUser);
    this.masq.initializeAfterAccountAvailable();

    // Fetch workspaces from Hydra if Journeys is not available
    if (!this.account.isJourneysAvailable) {
      // Get hydra workspaces and push them to the store so that
      // CDP only accounts/workspaces can access workspace settings
      let hydraWorkspaces = await this.api.request<HydraWorkspace[]>('workspaces', { pathScope: 'hydra_account' });

      this.store.pushPayload({
        environment: hydraWorkspaces.map((w) => ({
          id: `${w.id}`,
          name: w.name,
          hex_code: w.color,
          account_id: this.account.current.id,
        })),
      });
    }

    this.setupRaven(account);
    this.setupFeatureFlags(account);

    // Inject any tracking scripts needed for the authenticated state.
    if (this.session.isAuthenticated) {
      tracking.onAppLoad({ authenticated: true, account });
    }

    this.setupFullstory(account);

    // if current account requires 2FA and current user doesn't have it
    // enabled: redirect to the 2FA setup wizard until 2FA is set-up for
    // current user: all API calls would return 401 unauthorized response
    if (
      this.account.current.twoFactorAuthenticationRequired &&
      !this.auth.user?.twoFactorAuthenticationEnabled &&
      !this.masq.isOn
    ) {
      this.router.transitionTo('authenticated.2fa_setup');
    }
    if (this.masq.isOn && ((this.auth.user?.noMasqAccess ?? false) || (this.auth.user?.masqLevelNotSet ?? false))) {
      this.router.transitionTo('settings', {
        queryParams: {
          masqAccountID: this.masq.masqueradedAs,
        },
      });
    }
  }

  setupController(controller: AuthenticatedController, account: AccountModel): void {
    controller.set('account', account);

    if (!this.masq.isOn && account.confirmed) {
      this.satismeter.register(this.auth.user);
    }

    this.masq.initializeAfterAuthenticatedControllerReady(controller);
  }

  @action
  error(this: AuthenticatedRoute, _error: any) {
    // If there was an error, make sure to clear the account timeout and remove
    // the timeout class (if it exists). Then allow the error to bubble up to
    // the application route, which will handle the rest (like redirects).
    this.clearTimeout();
    return true;
  }

  private setupTimeout() {
    // If fetching the account does not respond within 10 seconds, the chances
    // of it responding favorably are unlikely so we must handle that scenario
    // outside of Ember because the application route does not have an error
    // sub-state.

    this.set(
      'accountTimeoutTimer',
      later(() => {
        (document.querySelector('body') as HTMLBodyElement).classList.add(bodyTimeoutClass);
      }, config.APP.accountTimeout),
    );
  }

  private clearTimeout() {
    cancel(this.accountTimeoutTimer);
    (document.querySelector('body') as HTMLBodyElement).classList.remove(bodyTimeoutClass);
  }

  private setupRaven(account: AccountModel) {
    this.raven.setUser({
      id: account.id,
      email: account.email,
    });

    this.raven.setFullstorySessionUrl();
  }

  private setupFeatureFlags(account: AccountModel) {
    this.persistentFeatures.loadFeatures();
    let featureFlags = account.featureFlags || {};

    Object.keys(featureFlags).forEach((flag) => {
      if (featureFlags[flag]) {
        this.features.enable(flag);
      } else {
        this.features.disable(flag);
      }
    });
  }

  private setupFullstory(account: AccountModel) {
    if (!window.FS?.identify) {
      return;
    }

    // Fullstory is inject by Hydra. It's OK to identify again
    // since we're using the same user id. This allow to collect
    // extra data as the dataCenter.
    let { currentUser } = account;

    window.FS.identify(currentUser.id, {
      email: currentUser.email,
      displayName: currentUser.fullName,
      dataCenter: account.dataCenter,
    });
  }
}
